import { MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import React from 'react';
import { useShefProfileNav } from 'src/pages/consumer/meal-plans/meal-plan-intake/your-shefs/hooks/useShefProfileNav';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { EllipsedEdgeColor, EllipsedEdgeContainer } from 'web-common/src/shared/design-system/EllipsedEdge';
import { DishModalContent } from './DishModalContent';

interface ShefBioSectionProps {
  shefId: string;
  bio?: string | null;
  imageUrl?: string | null;
  publicName: string;
  isCartEnabled?: boolean;
  analyticsLocation?: MealPlanAnalyticsLocation;
}

export const ShefBioSection = React.forwardRef<HTMLDivElement, ShefBioSectionProps>(
  ({ shefId, bio, imageUrl, publicName, isCartEnabled = false, analyticsLocation = 'dish-modal' }, ref) => {
    const { openShefProfileView, navigateToShefMenu } = useShefProfileNav({ analyticsLocation });

    const handleSeeMenu = () => {
      if (isCartEnabled) {
        openShefProfileView(shefId);
      } else {
        navigateToShefMenu(shefId);
      }
    };

    const renderShefImage = (
      <DishModalContent>
        <img
          className='absolute -top-2.5 z-menu-bar size-[100px] rounded-lg object-cover'
          src={imageUrl ? generateThumborImgUrl({ src: imageUrl, width: 100, height: 100 }) : ''}
          alt={`Shef ${publicName}`}
        />
      </DishModalContent>
    );

    return (
      <div ref={ref} className='relative bg-rice text-left text-milk'>
        {/* Shef profile picture extending above the curve */}
        {imageUrl && renderShefImage}
        <EllipsedEdgeContainer className='pb-24 pt-16' color={EllipsedEdgeColor.EGGPLANT}>
          <DishModalContent className='flex flex-col items-start'>
            {/* About Shef Header */}
            <p className='mt-12 text-h4-bold uppercase'>About Shef {publicName}</p>

            {/* Bio Description */}
            <p className='mb-4 mt-2.5 text-body-400'>{bio}</p>

            {/* See Menu Button */}
            <Button
              buttonType={ButtonType.TextLink}
              darkBackground
              onClick={handleSeeMenu}
              iconName='arrowRight'
              iconPosition='right'>
              See {publicName}'s menu
            </Button>
          </DishModalContent>
        </EllipsedEdgeContainer>
      </div>
    );
  }
);

ShefBioSection.displayName = 'ShefBioSection';
